import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Globe, Loader2, ShieldCheck, Store } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR } from "../lib/api";
import SiteAffiliateFlow from "./SiteAffiliateFlow";

export function SiteAffiliateLegacyRedirect({ kind }) {
  const [params] = useSearchParams();
  const q = new URLSearchParams();
  if (params.get("ref")) q.set("ref", params.get("ref"));
  q.set("kind", kind);
  return <Navigate to={`/create?${q.toString()}`} replace />;
}

export default function SiteAffiliateLanding() {
  const [params, setParams] = useSearchParams();
  const ref = (params.get("ref") || "").toUpperCase();
  const kind = params.get("kind") || "";
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    if (kind) return;
    Promise.all([
      api.get("/site-affiliate/config", { params: { kind: "website", ref } }),
      api.get("/site-affiliate/config", { params: { kind: "store", ref } }),
    ]).then(([w, s]) => setCfg({ website: w.data, store: s.data }))
      .catch((e) => toast.error(errMsg(e)));
  }, [kind, ref]);

  if (kind === "website" || kind === "store") return <SiteAffiliateFlow kind={kind} />;

  if (!cfg) return <div className="min-h-screen grid place-items-center" data-testid="sa-landing-loading"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>;
  if (!cfg.website.enabled && !cfg.store.enabled) {
    return <div className="min-h-screen grid place-items-center p-6 text-center"><p className="font-display font-bold text-lg">Direct website/store creation is currently unavailable.</p></div>;
  }

  const choose = (k) => setParams({ ref, kind: k });

  return (
    <div className="min-h-screen bg-[#f6f8f7]" data-testid="sa-landing">
      <header className="border-b bg-white">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="font-display font-extrabold text-lg tracking-tight">MILTROO</Link>
          {cfg.website.ref.valid && <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full" data-testid="sa-ref-badge">Referred by {cfg.website.ref.creator_name}</span>}
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-12 sm:py-16">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">Create your Website / Online Store</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-2">One product — your own business website with a full online store. No partner needed, build directly on MILTROO.</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
          className="max-w-md mx-auto bg-white border border-border rounded-3xl p-7 shadow-sm hover:border-emerald-500 transition-colors" data-testid="sa-option-site">
          <div className="h-12 w-12 rounded-2xl flex items-center justify-center text-white mb-4" style={{ background: "linear-gradient(135deg,#007A4D,#059669)" }}>
            <Store className="h-6 w-6" />
          </div>
          <h2 className="font-display font-extrabold text-xl tracking-tight">Business Website + Store</h2>
          <p className="text-sm text-muted-foreground mt-1.5">Your branding, pages, products, cart, checkout and order management — everything included.</p>
          <p className="mt-4 font-extrabold text-lg" data-testid="sa-price-site">{fmtINR(cfg.store.price)}<span className="text-xs font-semibold text-muted-foreground"> / year</span></p>
          <button onClick={() => choose("store")} data-testid="sa-continue-site"
            className="mt-4 w-full h-11 rounded-full text-white font-bold inline-flex items-center justify-center gap-1 transition-transform hover:scale-[1.02]"
            style={{ background: "linear-gradient(135deg,#007A4D,#059669)" }}>
            Continue <ChevronRight className="h-4 w-4" />
          </button>
        </motion.div>

        <p className="text-xs text-muted-foreground flex items-center justify-center gap-1.5 mt-8">
          <ShieldCheck className="h-4 w-4 text-emerald-600" />Hosting, SSL and builder access included. Manual payment verification by our team.
        </p>
      </div>
    </div>
  );
}
